import React, { useState } from 'react';
import { ProductModel, VariantSKU } from '../types';
import { Sparkles, Eye, Check } from 'lucide-react';

interface ProductCardProps {
  product: ProductModel;
  skus: VariantSKU[];
  onSelectProduct: (product: ProductModel, initialColorIndex?: number) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  skus,
  onSelectProduct,
}) => {
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);
  const activeColor = product.colors[selectedColorIdx] || product.colors[0];

  // คำนวณสต็อกรวมของสินค้ารุ่นนี้
  const productSkus = skus.filter((s) => s.productModelId === product.id);
  const totalStock = productSkus.reduce((sum, s) => sum + (s.physicalStock - s.reservedStock), 0);
  const isOutOfStock = totalStock <= 0;

  // รายการไซส์ที่มีของ
  const availableSizes = product.sizes.filter((size) => {
    return productSkus.some(
      (s) => s.sizeCode === size && (s.physicalStock - s.reservedStock) > 0
    );
  });

  return (
    <div
      onClick={() => onSelectProduct(product, selectedColorIdx)}
      className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl hover:border-green-200 transition-all duration-300 flex flex-col cursor-pointer"
    >
      {/* Product Image */}
      <div className="relative aspect-[4/5] bg-gray-100 overflow-hidden">
        <img
          src={activeColor.image}
          alt={product.name}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          {product.isPopular && (
            <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm">
              <Sparkles className="w-3 h-3" /> ยอดนิยม
            </span>
          )}
          <span className="bg-gray-900/80 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded-md">
            {product.category}
          </span>
        </div>

        {/* Quick View Button on Hover */}
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <span className="bg-white/95 text-gray-900 text-xs font-semibold px-4 py-2 rounded-full shadow-lg flex items-center gap-1.5 transform translate-y-2 group-hover:translate-y-0 transition-transform">
            <Eye className="w-3.5 h-3.5 text-green-600" /> เลือกขนาด & สั่งซื้อ
          </span>
        </div>

        {/* Out of Stock Overlay */}
        {isOutOfStock && (
          <div className="absolute inset-0 bg-gray-950/70 backdrop-blur-[2px] flex items-center justify-center">
            <span className="bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-md">
              สินค้าหมดชั่วคราว
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex-1 flex flex-col justify-between">
        <div>
          {/* Color Swatches */}
          <div className="flex items-center gap-1.5 mb-2.5">
            {product.colors.map((color, idx) => (
              <button
                key={color.name}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedColorIdx(idx);
                }}
                className={`w-4 h-4 rounded-full border transition-all ${
                  selectedColorIdx === idx
                    ? 'ring-2 ring-green-600 ring-offset-1 scale-110'
                    : 'border-gray-300 hover:scale-110'
                }`}
                style={{ backgroundColor: color.hex }}
                title={color.name}
              />
            ))}
            <span className="text-[11px] text-gray-400 ml-1">
              {product.colors.length} สี
            </span>
          </div>

          {/* Product Name */}
          <h3 className="font-semibold text-gray-900 text-sm line-clamp-1 group-hover:text-green-700 transition-colors">
            {product.name}
          </h3>

          {/* Available Sizes Badge */}
          <div className="flex flex-wrap gap-1 mt-2">
            {product.sizes.map((s) => {
              const isAvailable = availableSizes.includes(s);
              return (
                <span
                  key={s}
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    isAvailable
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-gray-50 text-gray-300 line-through'
                  }`}
                >
                  {s}
                </span>
              );
            })}
          </div>
        </div>

        {/* Price & Shipping */}
        <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between">
          <div>
            <span className="text-xs text-gray-400 font-normal">ราคา</span>
            <div className="text-base font-bold text-gray-900">
              ฿{product.basePrice.toLocaleString()}
            </div>
          </div>
          {product.basePrice >= 800 ? (
            <span className="text-[10px] font-semibold text-green-700 bg-green-50 px-2 py-1 rounded-md border border-green-200/60">
              ส่งฟรี
            </span>
          ) : (
            <span className="text-[10px] text-gray-400">
              ค่าส่ง ฿50
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
